import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
	CEX_ROUTES_SCHEME,
	DEX_NE_ROUTES_SCHEME,
	DEX_ROUTES_SCHEME,
	DEX_V2_ROUTES_SCHEME,
	MAIN_PAGES_ROUTES_SCHEME,
	MARKET_ROUTES_CONTENT_SCHEMA,
	PARTY_ROUTES_SCHEME,
} from "@/app/routing/route-scheme";
import { IPathParams, IRouteConfig, LayerType } from "@/app/routing/types";
import { BotType } from "@/shared/routing";

import { useGetNavigationLayer } from "./hooks/use-get-navigation-layer";
import { useGetNavigationList } from "./hooks/use-get-navigation-list";
import { AsideContext } from "./model/navigation-menu/context/context";
import { NavigationMenuProvider } from "./model/navigation-menu/context/navigation-menu";
import { NavigationMenu } from "./ui/navigation-menu/navigation-menu";

interface AsideProps {
	isMobile?: boolean;
	afterClick?: () => void;
}

const getPathKey = (pathname: string) =>
	pathname
		.split("/")
		.filter((segment) => !segment.startsWith(":") && segment !== "")
		.slice(-1)[0];

const BOTS_MENU_ITEMS_MAP: Record<BotType, IRouteConfig[]> = {
	CEX: CEX_ROUTES_SCHEME[0].children || [],
	DEX: DEX_ROUTES_SCHEME[0].children || [],
	DEX_V2: DEX_V2_ROUTES_SCHEME[0].children || [],
	DEX_NE: DEX_NE_ROUTES_SCHEME[0].children || [],
};

const LAYER_MENU_MAP: Record<LayerType, IRouteConfig[]> = {
	main: MAIN_PAGES_ROUTES_SCHEME,
	party: PARTY_ROUTES_SCHEME,
	market: MARKET_ROUTES_CONTENT_SCHEMA,
	"cex-bot": BOTS_MENU_ITEMS_MAP.CEX,
	"dex-bot": BOTS_MENU_ITEMS_MAP.DEX,
	"dex-v2-bot": BOTS_MENU_ITEMS_MAP.DEX_V2,
	"dex-ne-bot": BOTS_MENU_ITEMS_MAP.DEX_NE,
};

const AsideRender = ({ isMobile }: Pick<AsideProps, "isMobile">) => {
	const pathParams = useParams() as IPathParams;
	const { pathname } = useLocation();

	const layer = useGetNavigationLayer();
	const currentPathKey = getPathKey(pathname);
	const menuItems = useGetNavigationList(LAYER_MENU_MAP[layer], pathParams);

	return (
		<NavigationMenu
			menuItems={menuItems}
			activeMenuItemKey={currentPathKey}
			layer={layer}
			isMobile={isMobile}
		/>
	);
};

export const Aside = ({ isMobile, afterClick }: AsideProps) => {
	const eventsHandlers = useMemo(() => ({ afterClick }), [afterClick]);

	return (
		<AsideContext.Provider value={eventsHandlers}>
			<NavigationMenuProvider>
				<AsideRender isMobile={isMobile} />
			</NavigationMenuProvider>
		</AsideContext.Provider>
	);
};
